import { ExclamationTriangleIcon } from '@heroicons/react/24/solid';
import { DynamicPage, DynamicPageGoal } from '@superfunnel/types';
import Badge from '@superfunnel/ui-core/v2/Badge';
import Button from '@superfunnel/ui-core/v2/Button';
import DotStatus from '@superfunnel/ui-core/v2/DotStatus';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@superfunnel/ui-core/v2/Tooltip';
import { ColumnDef, Table } from '@tanstack/react-table';
import { QueryDocumentSnapshot } from 'firebase/firestore';
import {
  forwardRef,
  ReactElement,
  useContext,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { dynamicPageGoalsMap } from '../constants/dynamic-page';
import { useAnalytics } from '../contexts/AnalyticsContext';
import { DashboardContext } from '../contexts/DashboardContext';
import { OverviewContext } from '../contexts/OverviewContext';
import { UserContext } from '../contexts/UserContext';
import { classNames, getAnalyticsAttributes } from '../utils';
import { StatCellValues } from './analytics/StatCellValues';
import {
  CustomDataGrid,
  CustomDataGridHandle,
} from './CustomDataGrid/CustomDataGrid';

export type DynamicPageTableItem = QueryDocumentSnapshot<
  DynamicPage,
  DynamicPage
>;

export type DynamicPagesTableHandle = {
  getTableInstance: () => Table<DynamicPageTableItem> | undefined;
};

type DynamicPageStatus = DynamicPage['status'];

const statusConfig: Record<
  DynamicPageStatus,
  { label: string; variant: 'secondary' | 'success' | 'warning' }
> = {
  draft: { label: 'Draft', variant: 'secondary' },
  active: { label: 'Active', variant: 'success' },
  disabled: { label: 'Inactive', variant: 'warning' },
  deleted: { label: 'Deleted', variant: 'secondary' },
};

export function DynamicPageStatusBadge({
  status,
}: {
  status: DynamicPageStatus;
}) {
  const config = statusConfig[status] ?? statusConfig.draft;
  return (
    <Badge variant={config.variant} className="font-inter font-normal">
      {config.label}
    </Badge>
  );
}

interface Props {
  items: DynamicPageTableItem[] | undefined;
  actions?: (item: DynamicPageTableItem, index: number) => ReactElement<any>;
  onRowClick?: (dynamicPage: DynamicPageTableItem) => void;
  hideAnalytics?: boolean;
  enableRowSelection?: boolean;
  onRowSelectionChange?: (selectedRows: DynamicPageTableItem[]) => void;
}

export const DynamicPagesTable = forwardRef<DynamicPagesTableHandle, Props>(
  function DynamicPagesTable(
    {
      items,
      actions,
      onRowClick,
      hideAnalytics,
      enableRowSelection,
      onRowSelectionChange,
    }: Props,
    ref,
  ) {
    const navigate = useNavigate();
    const { slug } = useParams();
    const { track } = useAnalytics();
    const tableRef = useRef<CustomDataGridHandle<DynamicPageTableItem>>(null);

    const { analyticsCustomEvents, analyticsExternalPixelEvents } =
      useContext(UserContext);
    const { analyticsType } = useContext(DashboardContext);
    const { customRangeAnalytics } = useContext(OverviewContext);

    const getItemMetadataCached = useMemo(() => {
      const _cache = new Map<string, ReturnType<typeof _getMetadata>>();

      const _getMetadata = (dynamicPage: DynamicPageTableItem) => {
        const campaignData = dynamicPage.data();

        const _analyticsData = customRangeAnalytics;

        const analyticsData = _analyticsData
          ? (_analyticsData?.campaign?.[dynamicPage.id] ?? {})
          : undefined;

        const analyticsKey = 'custom';

        const campaignExtra = getDynamicPageAttributes(
          dynamicPage.id,
          campaignData,
          customRangeAnalytics,
        );

        return {
          campaignData,
          campaignExtra,
          analyticsData,
          _analyticsData,
          analyticsKey,
        };
      };

      return (dynamicPage: DynamicPageTableItem) => {
        const existing = _cache.get(dynamicPage.id);
        if (!existing) {
          const md = _getMetadata(dynamicPage);
          _cache.set(dynamicPage.id, md);
          return md;
        }

        return existing;
      };
    }, [customRangeAnalytics]);

    const columns: ColumnDef<DynamicPageTableItem>[] = useMemo(() => {
      return [
        {
          id: 'name',
          header: 'Name',
          accessorFn: (row) => row.data().name,
          size: 350,
          cell: ({ row: { original: dynamicPage } }) => (
            <div>
              <div className="font-inter flex items-end gap-1">
                <a
                  className="hover:text-brand-500 font-medium text-gray-900 hover:underline"
                  href={`/workspace/${slug}/pages/${dynamicPage.id}`}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();

                    track('dynamic_page_name_link_clicked', {
                      dynamic_page_id: dynamicPage.id,
                      dynamic_page_name: dynamicPage.data().name,
                      action: 'open_new_tab',
                    });

                    window.open(
                      `/workspace/${slug}/pages/${dynamicPage.id}`,
                      '_blank',
                    );
                  }}
                  target="_blank"
                  rel="noreferrer"
                >
                  {dynamicPage.data().name}
                </a>
                {!Object.keys(dynamicPage.data().connections ?? {})?.length &&
                  dynamicPage.data().status !== 'draft' && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <ExclamationTriangleIcon className="-m-1 h-6 w-6 p-1 text-yellow-400" />
                      </TooltipTrigger>
                      <TooltipContent onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-1">
                          <span className="align-middle text-sm font-medium">
                            Connect your campaign to see live data from Google
                          </span>

                          <Button
                            size="sm"
                            variant="ghost"
                            color="white"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();

                              track('connect_ad_account_clicked', {
                                dynamic_page_id: dynamicPage.id,
                                dynamic_page_name: dynamicPage.data().name,
                                source: 'table_warning',
                              });

                              navigate(
                                `/workspace/${slug}/pages/${dynamicPage.id}?tab=settings&settingsTab=ad-account`,
                              );
                            }}
                          >
                            Connect Now
                          </Button>
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  )}
              </div>
              <div className="text-base-500 font-inter mt-0.5 text-xs">
                /{dynamicPage.data().path}
              </div>
            </div>
          ),
        },
        {
          id: 'status',
          header: 'Status',
          sortingFn: 'alphanumeric',
          accessorFn: (row) => row.data().status,
          size: 90,
          cell: ({ row: { original } }) => {
            const { campaignData } = getItemMetadataCached(original);
            return <DynamicPageStatusBadge status={campaignData.status} />;
          },
        },
        {
          id: 'changes',
          header: 'Changes',
          accessorFn: (row) => row.data().hasChangesToPublish ?? false,
          size: 120,
          cell: ({ row: { original } }) => {
            const hasChangesToPublish = original.data().hasChangesToPublish;
            return (
              <div className="font-inter flex items-center gap-2">
                <DotStatus
                  color={hasChangesToPublish ? 'yellow' : 'green'}
                  animate={hasChangesToPublish}
                />
                <span className="text-gray-900">
                  {hasChangesToPublish ? 'Pending' : 'Deployed'}
                </span>
              </div>
            );
          },
        },
        {
          id: 'goal',
          header: 'Goal',
          accessorFn: (row) => row.data().goal,
          size: 120,
          cell: ({ row: { original } }) => {
            const { campaignData } = getItemMetadataCached(original);
            return (
              <div className="font-inter text-gray-900">
                {dynamicPageGoalsMap[campaignData.goal as DynamicPageGoal]}
              </div>
            );
          },
        },

        ...(!hideAnalytics
          ? ([
              {
                id: 'visitors',
                header: 'Visitors',
                accessorFn: (row) => {
                  const { analyticsData } = getItemMetadataCached(row);
                  return analyticsData?.uniqueVisitors?.custom ?? 0;
                },
                size: 120,
                cell: ({ row: { original } }) => {
                  const { analyticsData, analyticsKey } =
                    getItemMetadataCached(original);
                  return (
                    <StatCellValues
                      analyticsType={analyticsType}
                      analyticsKey={analyticsKey}
                      analyticsData={analyticsData}
                      format={(attrs) => attrs.visitors ?? '-'}
                      showAll={false}
                      fill
                    />
                  );
                },
              },
              {
                id: 'conversions',
                header: 'Conversions',
                accessorFn: (row) => {
                  const { analyticsData } = getItemMetadataCached(row);
                  return analyticsData?.uniqueConversions?.custom ?? 0;
                },
                size: 120,
                cell: ({ row: { original } }) => {
                  const { analyticsData, analyticsKey } =
                    getItemMetadataCached(original);
                  return (
                    <StatCellValues
                      analyticsType={analyticsType}
                      analyticsKey={analyticsKey}
                      analyticsData={analyticsData}
                      format={(attrs) => attrs.conversions ?? '-'}
                      showAll={false}
                      fill
                    />
                  );
                },
              },
              {
                id: 'allConversions',
                header: 'All Conversions',
                accessorFn: (row) => {
                  const { analyticsData } = getItemMetadataCached(row);
                  return analyticsData?.uniqueConversions?.totalCustom ?? 0;
                },
                size: 140,
                cell: ({ row: { original } }) => {
                  const { analyticsData, analyticsKey } =
                    getItemMetadataCached(original);
                  return (
                    <StatCellValues
                      analyticsType={analyticsType}
                      analyticsKey={analyticsKey}
                      analyticsData={analyticsData}
                      format={(attrs) => attrs.allConversions ?? '-'}
                      showAll={false}
                      fill
                    />
                  );
                },
              },
              {
                id: 'cr',
                header: 'Conv. rate',
                accessorFn: (row) => {
                  const { analyticsData } = getItemMetadataCached(row);
                  const visitors = analyticsData?.uniqueVisitors?.custom ?? 0;
                  const conversions =
                    analyticsData?.uniqueConversions?.custom ?? 0;
                  const cr = visitors > 0 ? (conversions / visitors) * 100 : 0;
                  return cr;
                },
                size: 170,
                cell: ({ row: { original } }) => {
                  const { analyticsData, analyticsKey } =
                    getItemMetadataCached(original);
                  return (
                    <StatCellValues
                      analyticsType={analyticsType}
                      analyticsKey={analyticsKey}
                      analyticsData={analyticsData}
                      format={(attrs) =>
                        attrs.visitors ? `${attrs.cr}%` : '-'
                      }
                      showAll={false}
                      fill
                    />
                  );
                },
              },
              {
                id: 'buttonClicks',
                header: 'Button Clicks',
                accessorFn: (row) => {
                  const { analyticsData } = getItemMetadataCached(row);
                  return analyticsData?.uniqueButtonClicks?.custom ?? 0;
                },
                size: 170,
                cell: ({ row: { original } }) => {
                  const { analyticsData, analyticsKey } =
                    getItemMetadataCached(original);
                  return (
                    <StatCellValues
                      analyticsType={analyticsType}
                      analyticsKey={analyticsKey}
                      analyticsData={analyticsData}
                      format={(attrs) => attrs.buttonClicks ?? '-'}
                      showAll={false}
                      fill
                    />
                  );
                },
              },
              {
                id: 'uniqueLinkClicks',
                header: 'Unique link clicks',
                accessorFn: (row) => {
                  const { analyticsData } = getItemMetadataCached(row);
                  return analyticsData?.uniqueButtonClicks?.uniqueCustom ?? 0;
                },
                size: 170,
                cell: ({ row: { original } }) => {
                  const { analyticsData, analyticsKey } =
                    getItemMetadataCached(original);
                  return (
                    <StatCellValues
                      analyticsType={analyticsType}
                      analyticsKey={analyticsKey}
                      analyticsData={analyticsData}
                      format={(attrs) => {
                        const val = attrs.uniqueButtonClicks;
                        const percentageFormatter = new Intl.NumberFormat(
                          'en-US',
                          {
                            style: 'percent',
                            maximumFractionDigits: 0,
                          },
                        );
                        return (
                          <>
                            <span>{val ?? '-'}</span>
                            <span className="ms-1 text-xs opacity-50">
                              (
                              {percentageFormatter.format(
                                (val ?? 0) / (attrs.visitors || 1),
                              )}
                              )
                            </span>
                          </>
                        );
                      }}
                      showAll={false}
                      fill
                    />
                  );
                },
              },
              {
                id: 'formSubmissions',
                header: 'Form Submissions',
                accessorFn: (row) => {
                  const { analyticsData } = getItemMetadataCached(row);
                  return analyticsData?.uniqueFormSubmissions?.custom ?? 0;
                },
                size: 170,
                cell: ({ row: { original } }) => {
                  const { analyticsData, analyticsKey } =
                    getItemMetadataCached(original);
                  return (
                    <StatCellValues
                      analyticsType={analyticsType}
                      analyticsKey={analyticsKey}
                      analyticsData={analyticsData}
                      format={(attrs) => attrs.formSubmissions ?? '-'}
                      showAll={false}
                      fill
                    />
                  );
                },
              },

              ...(analyticsCustomEvents?.map(
                (event) =>
                  ({
                    id: `customEvents-${event.id.replace(/[^a-zA-Z0-9-_]/g, '_')}`,
                    header: `Event: ${event.name}`,
                    accessorFn: (row) => {
                      const { _analyticsData } = getItemMetadataCached(row);
                      const customEventsAnalyticsData = _analyticsData
                        ? (_analyticsData?.customEvents?.campaign?.[row.id] ??
                          {})
                        : undefined;
                      return customEventsAnalyticsData?.[event.id]?.custom ?? 0;
                    },
                    size: 250,
                    cell: ({ row: { original } }) => {
                      const { _analyticsData, analyticsData, analyticsKey } =
                        getItemMetadataCached(original);
                      return (
                        <StatCellValues
                          analyticsType={analyticsType}
                          analyticsKey={analyticsKey}
                          analyticsData={analyticsData}
                          analyticsCustomEventsData={
                            _analyticsData
                              ? (_analyticsData?.customEvents?.campaign?.[
                                  original.id
                                ] ?? {})
                              : undefined
                          }
                          analyticsCustomEvents={analyticsCustomEvents}
                          format={(attrs) => {
                            const percentageFormatter = new Intl.NumberFormat(
                              'en-US',
                              {
                                style: 'percent',
                                maximumFractionDigits: 0,
                              },
                            );
                            return (
                              <>
                                <span>{attrs[event.id] ?? '-'}</span>
                                <span className="ms-1 text-xs opacity-50">
                                  (
                                  {percentageFormatter.format(
                                    (attrs[event.id] ?? 0) /
                                      (attrs.visitors || 1),
                                  )}
                                  )
                                </span>
                              </>
                            );
                          }}
                          showAll={false}
                          fill
                        />
                      );
                    },
                  }) satisfies ColumnDef<DynamicPageTableItem>,
              ) ?? []),

              ...(analyticsExternalPixelEvents?.map(
                (event) =>
                  ({
                    id: `pixelevent-${event.pixelType}-${event.id.replace(/[^a-zA-Z0-9-_]/g, '_')}`,
                    header: `Event: ${event.name}`,
                    accessorFn: (row) => {
                      const { _analyticsData } = getItemMetadataCached(row);
                      const analyticsExternalPixelsData = _analyticsData
                        ? (_analyticsData?.externalPixels?.[event.pixelType]
                            ?.campaign?.[row.id] ?? {})
                        : undefined;
                      return (
                        analyticsExternalPixelsData?.[event.id]?.custom ?? 0
                      );
                    },
                    size: 250,
                    cell: ({ row: { original } }) => {
                      const { _analyticsData, analyticsData, analyticsKey } =
                        getItemMetadataCached(original);
                      return (
                        <div>
                          <StatCellValues
                            analyticsType={analyticsType}
                            analyticsKey={analyticsKey}
                            analyticsData={analyticsData}
                            analyticsCustomEventsData={
                              _analyticsData
                                ? (_analyticsData?.externalPixels?.[
                                    event.pixelType
                                  ]?.campaign?.[original.id] ?? {})
                                : undefined
                            }
                            analyticsCustomEvents={analyticsExternalPixelEvents}
                            format={(attrs) => {
                              const percentageFormatter = new Intl.NumberFormat(
                                'en-US',
                                {
                                  style: 'percent',
                                  maximumFractionDigits: 0,
                                },
                              );
                              return (
                                <>
                                  <span>{attrs[event.id] ?? '-'}</span>
                                  <span className="ms-1 text-xs opacity-50">
                                    (
                                    {percentageFormatter.format(
                                      (attrs[event.id] ?? 0) /
                                        (attrs.visitors || 1),
                                    )}
                                    )
                                  </span>
                                </>
                              );
                            }}
                            showAll={false}
                            fill
                          />
                        </div>
                      );
                    },
                  }) satisfies ColumnDef<DynamicPageTableItem>,
              ) ?? []),
            ] satisfies ColumnDef<DynamicPageTableItem>[])
          : []),

        {
          id: 'tags',
          header: 'Tags',
          accessorKey: 'campaignData.tags',
          enableSorting: false,
          size: 350,
          cell: ({ row: { original } }) => {
            return (
              <div>
                <div className="flex flex-wrap gap-1">
                  {original.data().tags?.map((t) => (
                    <Badge key={t.id} className={'font-inter'}>
                      {t.name}
                    </Badge>
                  ))}
                </div>
              </div>
            );
          },
        },
        ...(actions
          ? ([
              {
                id: 'actions',
                header: '',
                accessorKey: 'actions',
                enableSorting: false,
                size: 50,
                cell: ({ row: { original, index } }) =>
                  actions(original, index),
              },
            ] satisfies ColumnDef<DynamicPageTableItem>[])
          : []),
      ] satisfies ColumnDef<DynamicPageTableItem>[];
    }, [
      actions,
      analyticsCustomEvents,
      analyticsExternalPixelEvents,
      analyticsType,
      getItemMetadataCached,
      hideAnalytics,
      navigate,
      slug,
      track,
    ]);

    useImperativeHandle(ref, () => ({
      getTableInstance: () => tableRef.current?.table,
    }));

    const _items = useMemo(() => {
      if (!items) return undefined;
      return [...items];
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items, customRangeAnalytics]);

    return (
      <CustomDataGrid
        ref={tableRef}
        logicalName="DynamicPagesTable"
        columns={columns}
        data={_items}
        rowClassName={classNames(onRowClick && 'cursor-pointer')}
        onRowClick={onRowClick}
        enableRowSelection={enableRowSelection}
        onRowSelectionChange={onRowSelectionChange}
        enableSorting
        cellClassName={'font-inter'}
        initialState={{
          columnPinning: {
            right: ['actions'],
          },
          columnVisibility: {
            buttonClicks: false,
            formSubmissions: false,
            uniqueLinkClicks: false,
            allConversions: false,
          },
        }}
      />
    );
  },
);

export function getDynamicPageAttributes(
  dynamicPageId: string,
  dynamicPage: DynamicPage,
  customRangeAnalytics?: Record<string, any>,
) {
  const campaignData = dynamicPage;
  const analyticsData = customRangeAnalytics;
  const analyticsKey = 'custom';

  const analyticsAttrs = getAnalyticsAttributes(
    analyticsKey,
    analyticsData?.campaign?.[dynamicPageId],
  );

  const campaignStatus = campaignData.status;

  return {
    ...analyticsAttrs,
    campaignStatus,
    device: Object.fromEntries(
      Object.entries(
        analyticsData?.campaign?.[dynamicPageId]?.device ?? [],
      ).map(([k, v]) => [k, getAnalyticsAttributes(analyticsKey, v as any)]),
    ),
  };
}
